import { Component, EventEmitter, forwardRef, Input, Output } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import * as moment from 'moment';

export declare type DateType = 'start' | 'end';

@Component({
  selector: 'app-month-picker',
  templateUrl: './month-picker.component.html',
  styleUrls: ['./month-picker.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MonthPickerComponent),
      multi: true
    }
  ]
})
export class MonthPickerComponent {
  /**
   * @description Set the input date.
   * @type {string} 'YYYY-MM-DD'
   */
  @Input() set inputDate(value: string){
    if(value){
      const date = new Date(value);
      this.selectedMonth = this.months[date.getMonth()].name;
      this.selectedYear = date.getFullYear();
      this.selectedDate = this.months[date.getMonth()].name + '-' + this.selectedYear;
    }
  }

  /**
   * @description The type of date to be selected.
   * @type {DateType} 'start' | 'end'
   */
  @Input() dateType: DateType = 'start';

  /**
   * @description Set the min date.
   * @type {string} 'YYYY-MM-DD'
   */
  @Input() set min(value: string){
    if(value){
      this.minDate = new Date(value);
    }
  }

  /**
   * @description Set the max date.
   * @type {string} 'YYYY-MM-DD'
   */
  @Input() set max(value: string){
    if(value){
      this.maxDate = new Date(value);
    }
  }

  /**
   * @description Event emitter for the input date
   * @type {EventEmitter<string>} 'YYYY-MM-DD'
   */
  @Output() inputDateChange = new EventEmitter<string>(); 
  selectedMonth: string = ''; // The selected month
  selectedYear!: number; // The selected year
  currentYear: number = new Date().getFullYear(); // The current year
  yearView: boolean = false; // Indicates whether the year view is active

  months = [
    { name: 'Jan' },
    { name: 'Feb' },
    { name: 'Mar' },
    { name: 'Apr' },
    { name: 'May' },
    { name: 'Jun' },
    { name: 'Jul' },
    { name: 'Aug' },
    { name: 'Sep' },
    { name: 'Oct' },
    { name: 'Nov' },
    { name: 'Dec' },
  ]; // Array of months to display in the month view
  years: Array<number> = []; // Array of years to display in the year view

  selectedDate = ''; // The selected date
  minDate:any = null; // Min date for the month picker
  maxDate:any = null; // Max date for the month picker

  // These are the functions that the ControlValueAccessor expects to implement
  onChange = (value: any) => {};
  onTouched = () => {};

  /**
   * Checks if the previous button should be disabled based on the current view.
   * @returns boolean indicating whether the previous button should be disabled.
   */
  previousDisabled(): boolean {
    if(this.yearView){
      if(this.minDate){
        return this.years[0] <= this.minDate.getFullYear();
      }
      return false
    }
    return this.minDate && this.currentYear <= this.minDate.getFullYear();
  }

  /**
   * Determines whether the "Next" button should be disabled based on the current state of the month picker.
   * @returns A boolean value indicating whether the "Next" button should be disabled.
   */
  nextDisabled(): boolean {
    if(this.yearView){
      if(this.maxDate){
        return this.years[this.years.length-1] >= this.maxDate.getFullYear();
      }
      return false;
    }
    return this.maxDate && this.currentYear >= this.maxDate.getFullYear();
  }

  /**
   * Determines if a month is disabled based on the minimum and maximum dates.
   * @param i - The index of the month.
   * @returns A boolean indicating if the month is disabled.
   */
  monthDisabled(i: number): boolean {
    if(this.minDate && this.maxDate){
      return this.currentYear === this.minDate.getFullYear() && i < this.minDate.getMonth() ||
        this.currentYear === this.maxDate.getFullYear() && i > this.maxDate.getMonth();
    }
    else if(this.minDate){
      return this.currentYear === this.minDate.getFullYear() && i < this.minDate.getMonth();
    }
    else if(this.maxDate){
      return this.currentYear === this.maxDate.getFullYear() && i > this.maxDate.getMonth();
    }
    return false;
  } 

  /**
   * Selects a month based on the given index.
   * @param index - The index of the month to select.
   */
  selectMonth(index: number) {
    this.selectedYear = this.currentYear;
    this.selectedMonth =  this.months[index].name;
    this.selectedDate = this.months[index].name + '-' + this.selectedYear;
    const formattedDate = this.dateType === 'end' ? 
      moment({ year: this.selectedYear, month:index }).endOf('month').format('YYYY-MM-DD') : 
      moment({ year: this.selectedYear, month:index }).startOf('month').format('YYYY-MM-DD');
    this.onChange(formattedDate);
    this.inputDateChange.emit(formattedDate);
  }

  /**
   * Selects a year and updates the current year value.
   * @param year - The year to be selected.
   */
  selectYear(year: number) {
    if(year > 0){
      this.currentYear = year;
      this.yearView = false;
    }
  }

  /**
   * Decreases the year value in the month picker component.
   * If the year view is active, it updates the list of years to display the previous 12 years.
   * If the minimum date is set, it only includes years that are greater than or equal to the minimum date's year.
   * If the year view is not active, it simply decreases the current year value by 1.
   */
  previousYear() {
    if(this.yearView){
      let temp = this.years[0];
      this.years = [];
      for (let i = 12; i > 0; i--) {
        if(this.minDate){
          if(temp - i >= this.minDate.getFullYear()){
            this.years.push(temp - i);
          }
        }
        else{
          this.years.push(temp - i);
        }
      }
    }
    else{
      this.currentYear--;
    }
  }
  
  /**
   * Advances to the next year in the month picker.
   * If the year view is active, it updates the list of years to display.
   * If the maximum date is set, it only adds years that are less than or equal to the maximum date.
   * If the year view is not active, it increments the current year by one.
   */
  nextYear() {
    if(this.yearView){
      let temp = this.years[this.years.length - 1];
      this.years = [];
      for (let i = 1; i <= 12; i++) {
        if(this.maxDate){
          if(temp + i <= this.maxDate.getFullYear()){
            this.years.push(temp + i);
          }
        }
        else{
          this.years.push(temp + i);
        }
      }
    }
    else{
      this.currentYear++;
    }
  }

  /**
   * Sets the value of the month picker component.
   * @param value - The value to be set.
   */
  writeValue(value: any): void {
    if (value) {
      const date = moment(value);
      this.selectedYear = date.year();
      this.selectedMonth = this.months[date.month()].name;
      this.selectedDate = this.months[date.month()].name + '-' + this.selectedYear;
    }
    else{
      this.selectedYear = this.currentYear;
    }
  }

  /**
   * @description This is called by Angular when it needs to register the change handler
   * @param fn 
   */
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  /**
   * @description This is called by Angular when it needs to register the touched handler
   * @param fn 
   */
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  /**
   * Changes the view of the month picker component.
   * If the current view is year view, it switches to month view.
   * If the current view is month view, it switches to year view and populates the years array based on the minDate and maxDate properties.
   */
  changeView(): void {
    if(this.yearView) {
      this.yearView = false;
    }
    else{
      this.years = [];
      for (let i = 0; i < 12; i++) {
        if(this.minDate && this.maxDate ){
          if(this.currentYear + i >= this.minDate.getFullYear() && this.currentYear + i <= this.maxDate.getFullYear()){
            this.years.push(this.currentYear + i);
          }
        }
        else if(!this.minDate && this.maxDate){
          if(this.currentYear + i <= this.maxDate.getFullYear()){
            this.years.push(this.currentYear + i);
          }
        }
        else if(this.minDate && !this.maxDate){
          if(this.currentYear + i >= this.minDate.getFullYear()){
            this.years.push(this.currentYear + i);
          }
        }
        else{
          this.years.push(this.currentYear + i);
        }
      }
      this.yearView = true;

    }
  } 

  /**
   * Handles the dropdown toggle event.
   * @param open - A boolean indicating whether the dropdown is open or closed.
   */
  onDropdownToggle(open:boolean): void{
    if(open){
      this.currentYear = this.selectedYear;
    }
    else{
      this.yearView = false;
    }
  }

}
